// MeterDetailPage.tsx - Updated with useMeterList hook
import React, { useState, useEffect } from "react";
import { ArrowLeft, Edit, Wifi, Eye, AlertCircle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { Tabs } from "@shared/ui/tabs";
import { Button } from "@shared/ui/button";
import { IndividualMeter, MeterDetailData } from "../types";
import { GenericTable } from "@shared/components/GenericTable";
import { useMeterModelDetail,useMeterList } from "../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { toast } from "@shared/ui/use-toast";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";

export const MeterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();

  usePageViewTimer({pageName: `MX Model Viewed - ${id}`})

  // Use your actual API hooks
  const { data: meterData, isLoading, error } = useMeterModelDetail({
    remote_utility_id: remoteUtilityId,
    meter_model_id: id || '',
  });

  const { data: meterListData, isLoading: meterListLoading, error: meterListError } = useMeterList({
    remote_utility_id: remoteUtilityId,
    meter_model_id: id || '',
  });



  // Handle back navigation
  const handleBack = () => {
    navigate('/mx/manufacturer');
  };

  // Handle edit navigation
  const handleEdit = () => {
    if (id) {
      navigate(`/mx/manufacturer/edit/${id}`);
    }
  };

  // Handle retry functionality
  const handleRetry = () => {
    window.location.reload();
  };


  // Error Component
  const ErrorDisplay = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Meter</h2>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-4 justify-center">
          <Button variant="outline" onClick={handleBack}>
            Go Back
          </Button>
          <Button onClick={onRetry}>
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );

  // Technical Specifications Component - Updated to use snake_case API fields
  const TechnicalSpecifications = ({ specs }: { specs: any }) => {
    if (!specs) return <div>No technical specifications available</div>;
    

    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Dial Length</label>
            <p className="text-gray-900">{specs.dialLength || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Max Flow</label>
            <p className="text-gray-900">{specs.maxFlow || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Weight</label>
            <p className="text-gray-900">{specs.weight ? `${specs.weight} kg` : 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Dimensions (L × W × H)</label>
            <p className="text-gray-900">
              {specs.length && specs.width && specs.height 
                ? `${specs.length} × ${specs.width} × ${specs.height} mm`
                : 'N/A'
              }
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Material</label>
            <p className="text-gray-900">{specs.material || 'N/A'}</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Dial Count</label>
            <p className="text-gray-900">{specs.dialCount || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Connection Size</label>
            <p className="text-gray-900">{specs.connectionSize || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Accuracy</label>
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {specs.accuracy || 'N/A'}
            </Badge>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">IP Rating</label>
            <Badge variant="secondary" className="bg-gray-100 text-gray-800">
              {specs.ipRating || 'N/A'}
            </Badge>
          </div>
        </div>
      </div>
    );
  };

  // Smart Features Component - Updated to use snake_case API fields
  const SmartFeatures = ({ meterConfiguration }: { meterConfiguration: any }) => {
    if (!meterConfiguration) return <div>No smart features configuration available</div>;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Read Type</label>
              <Badge variant="secondary" className="bg-purple-50 text-purple-700 ml-2">
                {meterConfiguration.readType || 'N/A'}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Display Type</label>
              <p className="text-gray-900">{meterConfiguration.displayType || 'N/A'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Read Frequency</label>
              <p className="text-gray-900">{meterConfiguration.readFrequency || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Tamper Proof</label>
              <Badge variant="secondary" className={`${meterConfiguration.tamperProof ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {meterConfiguration.tamper_proof ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600 block mb-3">Features</label>
          <div className="space-y-2">
            {meterConfiguration.tamperProof && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-700">Tamper Proof Enabled</span>
              </div>
            )}
            {/* Add more features based on your API response */}
            {!meterConfiguration.tamperProof && (
              <p className="text-gray-500">No smart features configured</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Individual Meters List Component - Updated to use real API data
  const MetersList = () => {
    // Transform API data to match table structure
    const individualMeters: IndividualMeter[] = meterListData?.results?.map((meter: any) => ({
      id: meter.id,
      meterNo: meter.meterNumber || 'N/A',
      deviceNumber: meter.deviceNo || 'N/A',
      installationDate: meter.installedOn || 'N/A',
      meterAgeInYears: meter.meterAgeInYears,
      status: meter.status || 'N/A',
    })) || [];

    const tableColumns = [
      {
        key: "meterNo",
        header: "Meter No",
        sortable: true,
      },
      {
        key: "deviceNumber",
        header: "Device Number",
        sortable: true,
        render: (value: string) => (
          <span className="text-blue-600 font-mono text-sm">{value}</span>
        ),
      },
      {
        key: "installationDate",
        header: "Installation Date",
        sortable: true,
      },
      {
        key: "meterAgeInYears",
        header: "Age",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        render: (value: string) => {
          const getStatusBadge = (status: string) => {
            switch (status) {
              case "Assigned":
                return (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                    Assigned
                  </Badge>
                );
              case "Unassigned":
                return (
                  <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
                    Unassigned
                  </Badge>
                );
              case "Faulty":
                return (
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
                    Faulty
                  </Badge>
                );
              default:
                return <Badge variant="secondary">{status}</Badge>;
            }
          };
          return getStatusBadge(value);
        },
      },
    ];

    const tableActions = [
      {
        label: "View",
        icon: "Eye" as const,
        onClick: (row: any) => {
          if (row.meterNo ! == "N/A" || row.meterNo ! == "NA" ) {
            navigate(`/mx/device-management/meter/view/${row.id}`)
          }
          else {
toast({
  title: "Warning",
  description: "This device is currently in inventory, so no details can be displayed",
  variant: "destructive",
});
          }
        },
      },
    ];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Individual Meters ({meterListData?.count || 0})
          </h3>
        </div>
        <GenericTable
          data={individualMeters}
          columns={tableColumns}
          actions={tableActions}
          actionsType="icons"
          rowKey="id"
          loading={meterListLoading}
          emptyMessage={meterListError ? "Error loading meters" : "No individual meters found"}
          onPageChange={(page: number) => {
          }}
        />
      </div>
    );
  };

  // Show error state
  if (error) {
    return <></>;
  }

  // Show "not found" state
  if (!meterData) {
    return <></>;
  }

  // Tab configuration - MOVED HERE after data validation
  //   const isSmartMeter=meterData.result?.meterTypeDisplay === "Smart Meter";
  // const tabComponents = {
  //   technical: {
  //     label: "Technical Specifications",
  //     component: <TechnicalSpecifications specs={meterData?.result?.technicalSpecifications} />,
  //   },
  //   smart: {
  //     label: "Smart Features", 
  //     component: <SmartFeatures meterConfiguration={meterData.result?.technicalSpecifications?.meterConfiguration} />,
  //   },
  //   meters: {
  //     label: "Meters List",
  //     component: <MetersList />,
  //   },
  // };

  const isSmartMeter = meterData.result?.meterTypeDisplay === "Smart Meter";

const tabComponents: Record<string, { label: string; component: JSX.Element }> = {
  technical: {
    label: "Technical Specifications",
    component: (
      <TechnicalSpecifications
        specs={meterData?.result?.technicalSpecifications}
      />
    ),
  },
  meters: {
    label: "Meters List",
    component: <MetersList />,
  },
};

// ✅ Add smart tab only if Smart Meter
if (isSmartMeter) {
  tabComponents.smart = {
    label: "Smart Features",
    component: (
      <SmartFeatures
        meterConfiguration={
          meterData.result?.technicalSpecifications?.meterConfiguration
        }
      />
    ),
  };
}


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-gray-900">
                  {meterData?.result?.meterModel || 'Meter Model'}
                </h1>
                <div className="flex items-center gap-2">
                  <Wifi className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-gray-600">
                    {meterData.result?.meterManufacturer?.name || 'Unknown Manufacturer'}
                  </span>
                  <Badge className="bg-purple-100 text-purple-800">
                    {meterData.result?.meterTypeDisplay || 'Unknown Type'}
                  </Badge>
                  <Badge className="bg-black text-white">
                    {meterData.result?.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Basic Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600">Manufacturer</label>
                  <p className="text-gray-900 font-semibold">
                    {meterData?.result?.meterManufacturer?.name || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Model Name</label>
                  <p className="text-gray-900 font-semibold">
                    {meterData?.result?.meterModel || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Make Code</label>
                  <p className="text-gray-900 font-semibold">
                    {meterData?.result?.meterMake || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Meter Type</label>
                  <Badge className="bg-purple-100 text-purple-800">
                    {meterData?.result?.meterTypeDisplay || 'Unknown'}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Created By</label>
                  <p className="text-gray-900">{meterData?.result?.createdUserRemoteName || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Created On</label>
                  <p className="text-gray-900">
                    {meterData?.result?.createdDate || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Utilities</label>
                  <Badge className="bg-blue-100 text-blue-800">
                    {meterData?.result?.utilityService || 'N/A'}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Description</label>
                  <p className="text-gray-900 text-sm">
                    {meterData?.result?.description || 'No description available'}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <Tabs
                    defaultValue="technical"
                    tabComponents={tabComponents}
                    className="w-full"
                  />
                </div>
                <button 
                  onClick={handleEdit}
                  className="px-4 h-12 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Model
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Statistics and Commands */}
          <div className="space-y-6">
            {/* Meter Statistics */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Meter Statistics</h3>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Total Meters in System</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {meterListData?.count || '0'}
                  </p>
                </div>  
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};